import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, testimonials } from '@/mocks/siteData';

export default function AboutPage() {
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [formMessage, setFormMessage] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const formActionUrl = 'https://readdy.ai/api/form/d9q2sp7nahb7382ov5a0';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (name === 'message') setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormMessage('感谢您的留言，我们会尽快回复！');
      setFormStatus('success');
      return;
    }
    formData.delete('company_alt');
    try {
      const res = await fetch(formActionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormMessage('感谢您的留言，我们会尽快回复！');
        setFormStatus('success');
        setFormState({ name: '', email: '', message: '' });
        setCharCount(0);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试。';
        setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后重试。' : serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormMessage('网络错误，请稍后重试。');
      setFormStatus('error');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 白丝JK正版周边购买指南',
    description: `${siteInfo.name}品牌介绍、口碑评价与联系方式`,
    url: 'https://shiroi-jk.com/about',
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Organization',
      name: siteInfo.name,
      description: siteInfo.description,
      url: 'https://shiroi-jk.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <SEOHead
        title="关于我们"
        description={`${siteInfo.name}品牌介绍、用户口碑评价与联系方式，了解我们的使命与价值。`}
        keywords={`关于白丝jk,${siteInfo.name},白丝jk周边指南`}
        canonicalUrl="https://shiroi-jk.com/about"
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-6">
              关于我们
            </h1>
          </div>
        </div>

        <section className="max-w-4xl mx-auto px-4 md:px-6 py-14">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="lg:w-1/3">
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Cozy%20anime%20merchandise%20collection%20display%20with%20figures%20and%20art%20books%2C%20warm%20lighting%2C%20wooden%20shelves%2C%20soft%20pastel%20pink%20aesthetic%2C%20creative%20studio%20atmosphere%2C%20carefully%20curated%20collection&width=600&height=600&seq=about-brand&orientation=squarish"
                  alt="白丝JK周边品牌"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:w-2/3">
              <h2 className="font-heading text-2xl text-foreground-950 mb-4">品牌介绍</h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                白丝JK正版周边购买指南成立于2024年，是国内首个专注于白丝JK主题周边的综合性资讯平台。
                我们致力于为二次元爱好者提供最全面、最专业的白丝JK周边产品信息与购买建议。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                无论是手办收藏、JK制服穿搭、同人文化探索还是正版购买渠道推荐，
                我们都力求以客观、详实的内容帮助每一位爱好者做出明智的消费决策。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                我们的团队由资深动漫爱好者、手办收藏家和JK制服达人组成，
                每位成员都怀着对二次元文化的热爱，用心输出每一篇内容。
              </p>

              <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-background-200/70">
                <div className="text-center">
                  <div className="text-3xl font-heading text-primary-500 mb-1">500+</div>
                  <p className="text-xs text-foreground-500">收录周边</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-heading text-primary-500 mb-1">50+</div>
                  <p className="text-xs text-foreground-500">深度文章</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-heading text-primary-500 mb-1">10万+</div>
                  <p className="text-xs text-foreground-500">月访问用户</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl text-foreground-950 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-background-50 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{testimonial.name}</div>
                      <div className="text-xs text-foreground-400">{testimonial.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{testimonial.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 md:px-6 py-14">
          <h2 className="font-heading text-2xl text-foreground-950 mb-8 text-center">联系我们</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h3 className="font-heading text-lg text-foreground-900 mb-4">联系方式</h3>
              <ul className="space-y-4 text-sm text-foreground-600">
                <li className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <span>{siteInfo.email}</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <span>微信：{siteInfo.wechat}</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-weibo-line"></i>
                  </div>
                  <span>{siteInfo.weibo}</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-bilibili-line"></i>
                  </div>
                  <span>B站：{siteInfo.bilibili}</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-heading text-lg text-foreground-900 mb-4">发送消息</h3>
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                <div className="form-honeypot">
                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <input
                  type="text"
                  name="name"
                  value={formState.name}
                  onChange={handleInputChange}
                  placeholder="你的称呼"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
                <input
                  type="email"
                  name="email"
                  value={formState.email}
                  onChange={handleInputChange}
                  placeholder="你的邮箱"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
                <div className="relative">
                  <textarea
                    name="message"
                    value={formState.message}
                    onChange={handleInputChange}
                    placeholder="请输入你的留言（最多500字）"
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  />
                  <span className="absolute bottom-2 right-3 text-xs text-foreground-400">{charCount}/500</span>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  发送消息
                </button>
                {formMessage && (
                  <p className={`text-sm text-center ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}